import {useMemo} from 'react';
import {useWindowInnerHeight} from '../../../../../../common/shared/hooks/useWindowInnerHeight';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {TalkKatalkSceneHeader} from '../../../../../../design-system/organisms/TalkKatalkSceneHeader/TalkKatalkSceneHeader';
import {useViewerInfoQuery} from '../../../../../../wrapper/viewerWrapper';
import {
  BASE_VIEWER_WIDTH_PX,
  DISPLAY_AD_HEIGHT,
  IGNORE_GLOBAL_FONT_SET_CLASSNAME,
  KATALK_HEADER_HEIGHT,
} from '../constant/talkViewerConstant';
import {useViewerWidth} from '../hook/useViewerWidth.hook';
import {SceneKey} from '../type/KeyType.type';
import {SceneType} from '../type/SceneType.enum';
import {TalkViewerFontFamilyType} from '../type/TalkViewerFontFamilyType.enum';
import {TalkViewerGroupContainer} from './TalkViewerGroupContainer';

interface TalkViewerSceneContainerProps {
  sceneId: SceneKey;
}

export function TalkViewerSceneContainer({
  sceneId,
}: TalkViewerSceneContainerProps) {
  const {
    isViewerEndExposed,
    isViewerMenuExposed,
    viewerMenuHeight,
    seriesId,
    productId,
    scene,
    sceneInfo,
  } = useTypedSelector(state => {
    return {
      isViewerEndExposed: state.viewerEnd.isExposed,
      isViewerMenuExposed: state.viewerMenu.isExposed,
      viewerMenuHeight: state.talkViewer.settings.viewerMenuHeight,
      seriesId: state.viewerInfo.currentSeriesId,
      productId: state.viewerInfo.currentProductId,
      scene: state.talkViewer.sceneMap[sceneId],
      sceneInfo: state.talkViewer.sceneInfoMap[sceneId],
    };
  });
  const displayAd = useViewerInfoQuery(
    // @ts-ignore
    {seriesId, productId},
    {enabled: !!seriesId && !!productId}
  ).data?.displayAd;
  const isExposeDaInTalkViewer = !!productId && !!displayAd;
  const windowInnerHeight = useWindowInnerHeight();

  const {viewerWidth} = useViewerWidth();
  const viewerTopPadding = useMemo(() => {
    // 뷰어메뉴 높이 + 최소 상단 패딩으로 첫 톡이 뷰어메뉴에 가려지지 않도록 계산 된 기본 패딩값
    const defaultViewerTopPadding =
      viewerMenuHeight.top + DEFAULT_VIEWER_TOP_PADDING;
    // 카톡씬의 경우 별도의 카톡 헤더가 상단에 떠 있는데, 첫 톡이 이 헤더에 가려지지 않도록 계산 된 패딩값. 카톡씬이 아닌 경우는 항상 0
    const katalkSceneTopPadding =
      scene?.type === SceneType.Kakaotalk
        ? KATALK_HEADER_HEIGHT * (viewerWidth / BASE_VIEWER_WIDTH_PX)
        : 0;
    // 상단에 DA가 떠 있을 때 DA에 첫 톡이 가려지지 않도록 계산 된 패딩값. DA가 없으면 0
    const daTopPadding = isExposeDaInTalkViewer
      ? DISPLAY_AD_HEIGHT + DEFAULT_VIEWER_TOP_PADDING
      : 0;
    // 기본 패딩값과 DA/카톡씬으로 계산 된 패딩값중 큰 값을 사용. 첫 톡이 가려지면 안 되기 때문
    return (
      Math.max(
        defaultViewerTopPadding,
        // 카톡씬일 때에는 DA와 카톡헤더 두개 모두 뜬다. 그래서 패딩값을 합쳐서 계산
        katalkSceneTopPadding + daTopPadding
      ) +
      // 상단 safearea에는 톡이 보이면 안 되므로 safearea 상단값을 항상 더해준다
      (viewerMenuHeight.safeAreaTop || 0)
    );
  }, [
    viewerMenuHeight.top,
    viewerMenuHeight.safeAreaTop,
    scene?.type,
    viewerWidth,
    isExposeDaInTalkViewer,
  ]);

  if (!scene) {
    return null;
  }
  const isKatalkScene = scene.type === SceneType.Kakaotalk;
  return (
    <div
      // eslint-disable-next-line tailwindcss/no-custom-classname
      className={twMerge(
        'scene-container-wrapper',
        'relative mx-auto box-border flex w-full flex-col pb-360tvs',
        isViewerEndExposed && 'hidden',
        'pr-safe pl-safe'
      )}
      style={{
        minHeight: windowInnerHeight,
        paddingTop: viewerTopPadding,
      }}
    >
      {isKatalkScene && (
        <TalkKatalkSceneHeader
          isExposeDaInTalkViewer={isExposeDaInTalkViewer}
          isKatalkScene={isKatalkScene}
          isViewerMenuExposed={isViewerMenuExposed}
          title={scene.title}
          viewerMenuHeight={viewerMenuHeight}
        />
      )}
      {sceneInfo.talkGroupId && (
        <div className="flex-1">
          <TalkViewerGroupContainer
            sceneId={sceneId}
            talkGroupId={sceneInfo.talkGroupId}
          />
        </div>
      )}
      <style jsx>
        {`
          .scene-container-wrapper
            :global(*:not(.${IGNORE_GLOBAL_FONT_SET_CLASSNAME})) {
            font-family: ${TalkViewerFontFamilyType.Default} !important;
          }
        `}
      </style>
    </div>
  );
}

// 뷰어 메뉴와 너무 붙는걸 방지하기 위해 최소 패딩을 준다
const DEFAULT_VIEWER_TOP_PADDING = 10;
